// src/components/GuestCheckOut.js (FINAL FIXED)

import React, { useState } from "react";
import { db } from "../firebase";
import { collection, query, where, getDocs, doc, updateDoc } from "firebase/firestore";

import AIGuestAssistant from "../components/AIGuestAssistant";
import { useTranslation } from 'react-i18next';

// --- New Search Options for enhanced security ---
const SEARCH_OPTIONS = [
  { value: "bookingKey", label: "Booking Key" },
  { value: "accessKey", label: "Access Key" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone Number" },
];

export default function GuestCheckOut() {
  const [searchTerm, setSearchTerm] = useState("");
  // Default to a secure search type
  const [searchType, setSearchType] = useState("bookingKey"); 
  const [guestData, setGuestData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const { t } = useTranslation();
  // ✅ Calculate final charges and include add-ons
  const calculateFinalCharges = (roomPrice, totalDays, addOnsPrice = 0) => {
    // Default to 1 day if totalDays is not provided or is zero/negative
    const safeTotalDays = Math.max(1, totalDays || 1); 
    
    const baseRoomCharge = roomPrice * safeTotalDays;
    const vat = baseRoomCharge * 0.1;
    const cityTax = baseRoomCharge * 0.02;
    // CRITICAL CHANGE: Include staff-added Add-Ons price
    const subTotal = baseRoomCharge + vat + cityTax;
    const finalTotal = subTotal + addOnsPrice;
    
    return { baseRoomCharge, vat, cityTax, addOnsPrice, subTotal, finalTotal };
  };

  // ✅ Search for a guest using multiple fields
  const handleFindGuest = async (e) => {
    e.preventDefault();
    setGuestData(null);
    setError(null);
    setSuccess(null);
    setLoading(true);

    const term = searchTerm.trim();
    if (!term) {
      setLoading(false);
      return setError(t('checkout.err_enter_term', { type: searchType }));
    }

    try {
      let q;
      const termValue = (searchType === "email") ? term.toLowerCase() : term;
      
      // Construct the query based on the selected search type
      // NOTE: This will retrieve ALL bookings matching the search term (past and present)
      q = query(collection(db, "guests"), where(searchType, "==", termValue));

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setError(t('checkout.err_no_booking', { type: searchType }));
      } else {
        const guests = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        // ⭐ CRITICAL FIX: Sort guests to find the most recent booking, regardless of status.
        const sortedGuests = guests
          .filter(guest => guest.checkInDate) // Only consider guests with a check-in date
          .sort((a, b) => {
            // Robust date conversion handling both Firebase Timestamps and ISO strings
            const getDateValue = (dateField) => dateField.toDate 
              ? dateField.toDate().getTime() 
              : new Date(dateField).getTime();
            
            // Sort descending (b - a) to get the most recent check-in first
            return getDateValue(b.checkInDate) - getDateValue(a.checkInDate);
          });

        if (sortedGuests.length > 0) {
          // Set the most recent booking data for display
          setGuestData(sortedGuests[0]); 
        } else {
          // Fallback error if no valid bookings are found
          setError(t('checkout.err_process_failed'));
        }
      }
    } catch (err) {
      console.error("Error searching guest:", err);
      setError(t('checkout.err_firebase'));
    } finally {
      setLoading(false);
    }
  };

  // ✅ Request checkout
  const handleRequestCheckout = async () => {
    if (!guestData || guestData.status !== "CHECKED_IN") {
      return setError(t('checkout.err_not_checked_in'));
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const guestRef = doc(db, "guests", guestData.id);
      await updateDoc(guestRef, {
        status: "CHECKOUT_REQUESTED",
        checkoutRequestTimestamp: new Date().toISOString(),
      });

      setGuestData((prev) => ({ ...prev, status: "CHECKOUT_REQUESTED" }));
      setSuccess(t('checkout.success_request'));
    } catch (err) {
      console.error("Checkout request error:", err);
      setError(t('checkout.err_firebase'));
    } finally {
      setLoading(false);
    }
  };

  // Computed Properties for cleaner rendering
  const isCheckedIn = guestData && guestData.status === "CHECKED_IN";
  const isRequestPending = guestData && guestData.status === "CHECKOUT_REQUESTED";
  // The CHECKED_OUT status is now also relevant for viewing the final bill
  const isFinalized = guestData && (guestData.status === "CHECKOUT_FINALIZED" || guestData.status === "CHECKED_OUT");
  
  // Pass addOnsPrice to the calculator
  const charges = guestData 
    ? calculateFinalCharges(
        guestData.roomPrice, 
        guestData.totalDays, 
        guestData.addOnsPrice // Pass the staff-added charge
      ) 
    : null;

  return (
    <>
    <div className="container" style={{ paddingTop: "50px", maxWidth: "800px", margin: "0 auto" }}>
      <h2>{t('home.guest_checkout')}</h2>

      {error && <p className="error-message info-box" style={{ backgroundColor: 'pink', borderLeft: '5px solid red', padding: '10px' }}>{error}</p>}
      {success && <p className="success-message info-box" style={{ backgroundColor: '#d4edda', borderLeft: '5px solid green', padding: '10px' }}>{success}</p>}

      <form onSubmit={handleFindGuest} style={{ display: "flex", marginBottom: "30px" }}>
        <select
          value={searchType}
          onChange={(e) => {
            setSearchType(e.target.value);
            setSearchTerm("");
          }}
          style={{ marginRight: "10px", width: "150px" }}
        >
          {SEARCH_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {t(`checkout.label_${option.value}`, { defaultValue: option.label })}
            </option>
          ))}
        </select>
        <input
          type={searchType === "email" ? "email" : "text"} 
          placeholder={t('checkout.search_placeholder', { type: searchType })}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          required
          style={{ flexGrow: 1, marginRight: "10px" }}
        />
        <button type="submit" disabled={loading}>
          {loading ? t('checker.searching') : t('checkout.btn_find')}
        </button>
      </form>

      {guestData && (
        <div className="card" style={{ border: `1px solid ${isFinalized ? 'darkgreen' : isRequestPending ? 'orange' : 'green'}` }}>
          
          <h3>{t('checker.finalized_details_title')} - {t('checker.room_prefix')} {guestData.roomNumber} {guestData.roomNumber}</h3>
          
          {/* Helpful Feature: Quick info banner */}
          {isFinalized && (
             <div style={{ backgroundColor: '#e6ffec', border: '1px solid darkgreen', padding: '10px', marginBottom: '15px' }}>
                ✅ {t('checkout.msg_finalized')}
            </div>
          )}

          <p>
            {guestData.fullName} · {t('registration.section1_title')}:{" "}
            {/* Handle both Firebase Timestamps and ISO strings for display */}
            {new Date(guestData.checkInDate.toDate ? guestData.checkInDate.toDate() : guestData.checkInDate).toLocaleDateString()}
          </p>
          
          <p
            style={{
              color: isCheckedIn ? "green" : isRequestPending ? "orange" : isFinalized ? "darkgreen" : "red",
              fontWeight: "bold",
            }}
          >
            {t('guest_dash.status')}: <strong>{t(`checker.status_${guestData.status.toLowerCase()}`, { defaultValue: guestData.status.replace("_", " ") })}</strong>
          </p>

          {/* ✅ Display charges (Visible for all statuses after search) */}
          {charges && (
            <div
              className="charges-summary"
              style={{
                border: "1px dashed #ccc",
                padding: "15px",
                marginTop: "15px",
                backgroundColor: '#f8f8f8'
              }}
            >
              <h4>{t('checkout.summary_title')}</h4>
              <p>{t('registration.selected_room_price')}: ${charges.baseRoomCharge.toFixed(2)}</p>
              <p>{t('checkout.taxes_fees')}: ${ (charges.vat + charges.cityTax).toFixed(2) }</p>
              
              {/* NEW: Display Add-Ons price clearly */}
              <p style={{ color: charges.addOnsPrice > 0 ? 'red' : 'gray', fontWeight: 'bold' }}>
                {t('checkout.extra_charges')}: ${charges.addOnsPrice.toFixed(2)}
              </p>
              
              <h4
                style={{
                  borderTop: "1px solid #000",
                  paddingTop: "5px",
                  fontWeight: "bold",
                  color: isFinalized ? 'darkgreen' : 'black'
                }}
              >
                {t('checkout.total_due')}: ${charges.finalTotal.toFixed(2)}
              </h4>
              
              {isFinalized && (
                  <button 
                    onClick={() => alert("Invoice Downloaded (Placeholder)")} 
                    style={{ backgroundColor: 'darkgreen', color: 'white', marginTop: '10px' }}
                  >
                    {t('checker.btn_download_pdf')}
                  </button>
              )}

            </div>
          )}

          {/* ✅ Request checkout button - only visible if CHECKED_IN */}
          {isCheckedIn && (
            <button
              onClick={handleRequestCheckout}
              disabled={loading}
              style={{
                backgroundColor: "#ffc107",
                marginTop: "20px",
                fontWeight: "bold",
                color: '#333'
              }}
            >
              {loading ? t('ai_assistant.typing') : t('checkout.btn_submit')}
            </button>
          )}

          {/* Status Message */}
          {isRequestPending && (
            <div
              style={{
                backgroundColor: '#fffbe6',
                border: '1px solid orange',
                padding: '10px',
                marginTop: '20px'
              }}
            >
              <p style={{ margin: 0, color: "orange", fontWeight: "bold" }}>
                ⏳ {t('checkout.msg_pending_title')}:
              </p>
              <p style={{ margin: '5px 0 0 0', fontSize: '0.9em' }}>
                {t('checkout.msg_pending_desc')}
              </p>
            </div>
          )}
          
        </div>
      )}
      
      {/* Helpful Feature: Key drop-off instructions */}
      {(isRequestPending || isFinalized) && (
        <div className="card" style={{ marginTop: '20px', borderLeft: '5px solid blue' }}>
            <h4>{t('checkout.key_drop_title')}</h4>
            <p>{t('checkout.key_drop_desc')}</p>
        </div>
      )}
      
    </div>
    <AIGuestAssistant />
    </>
  );
}